import {z} from "zod";
import type {ConversationMessage, MessageContent, MessageContentFunctionCall, MessageContentFunctionResult} from "./types";

const record = z.record(z.string(), z.unknown());
const node = z.object({id: z.string().min(1), type: z.string().min(1), name: z.string().optional()}).passthrough();
export const omegaGraphSchema = z.object({
  nodes: z.array(node),
  edges: z.array(z.object({id: z.string(), from: z.string(), to: z.string()}).passthrough()),
}).passthrough();
const savedWorkflow = z.object({
  id: z.string().min(1), revision: z.union([z.string(), z.number()]), status: z.string(),
  name: z.string(), description: z.string().nullable().optional(), graph: omegaGraphSchema,
}).passthrough();
export type SavedOmegaWorkflow = z.infer<typeof savedWorkflow>;
const envelope = z.object({ok: z.boolean(), status: z.number().int().min(100).max(599), body: z.unknown()});
export function parseOmegaResult(output: unknown) {
  if (typeof output !== "string") return null;
  try {
    const parsed = envelope.safeParse(JSON.parse(output));
    return parsed.success ? parsed.data : null;
  } catch { return null; }
}
// Values are `ask` translation keys under `workflowPanel.activity`.
const phases: Record<string, string> = {
  omega_fetchallnodedefinitions: "discoveringNodes", omega_fetchnodedefinition: "discoveringNodes",
  omega_fetchcustomerworkflow: "readingWorkflow", omega_checkcustomerworkflow: "validatingGraph",
  omega_savedraftcustomerworkflow: "savingDraft", omega_fetchlastworkflowruns: "readingResults",
  omega_fetchworkflowrun: "readingResults", omega_startdebugsession: "startingTest",
  omega_rundebugsession: "runningTest", omega_stepdebugsession: "steppingTest", omega_stopdebugsession: "stoppingTest",
  omega_fetchallcustomerworkflows: "findingWorkflows",
};
/** Run states Omega reports on a debug session. */
const DEBUG_SESSION_STATUSES = ["paused", "stepping", "running", "completed", "cancelled", "failed", "timed_out"] as const;
/** A debug session's state, or what the conversation implies before Omega reports one. */
export type OmegaRunStatus = (typeof DEBUG_SESSION_STATUSES)[number] | "stopping" | "unknown";
const stateSchema = z.object({status: z.enum(DEBUG_SESSION_STATUSES)});
const traceSchema = z.object({runId: z.string(), workflowId: z.string(), type: z.string(), stepIndex: z.number(),
  nodeId: z.string().nullable().optional(), nodeName: z.string().nullable().optional(), isSuccess: z.boolean().optional(),
}).passthrough();
export type OmegaRun = {id: string; workflowId?: string; status: OmegaRunStatus; snapshot?: unknown; traces: z.infer<typeof traceSchema>[]};
/** `label` is an `ask` translation key under `workflowPanel.activity`, with `params` for interpolation. */
export type OmegaActivity = {id: string; label: string; params?: Record<string, string | number>; detail?: unknown; error?: boolean; pending?: boolean};

const SAVE_DRAFT_TOOL = "omega_savedraftcustomerworkflow";

export type OmegaProgressStep = {id: string; label: string; state: "pending" | "returned" | "error"};

/** Only the current turn drives live progress; old errors and calls must not look active again. */
export function collectOmegaProgress(messages: ConversationMessage[]) {
  let start = messages.length - 1;
  while (start >= 0 && messages[start].role !== "user") start--;
  const steps = new Map<string, OmegaProgressStep>();
  let next = "planning";
  const parts = messages.slice(start + 1).flatMap(message => message.contents);
  for (const part of parts) {
    if (!isOmegaToolPart(part) || !phases[part.functionName]) continue;
    if (part.type === "function_call") {
      if (!steps.has(part.callId)) steps.set(part.callId, {id: part.callId, label: phases[part.functionName], state: "pending"});
      continue;
    }
    const result = parseOmegaResult(part.result);
    const error = !result?.ok || result.status >= 400;
    steps.set(part.callId, {id: part.callId, label: phases[part.functionName], state: error ? "error" : "returned"});
    next = nextOmegaPhase(part.functionName, error);
  }
  const activity = [...steps.values()];
  const pending = activity.filter(step => step.state === "pending");
  return {steps: activity, pending, next, returned: activity.length - pending.length};
}

function nextOmegaPhase(functionName: string, error: boolean): string {
  if (error) return functionName === "omega_checkcustomerworkflow" ? "repairing" : "reviewing";
  return functionName === SAVE_DRAFT_TOOL ? "finishing" : "planning";
}

/** A call to, or result from, one of Wingman's compiled Omega tools. */
export function isOmegaToolPart(part: MessageContent): part is MessageContentFunctionCall | MessageContentFunctionResult {
  return (part.type === "function_call" || part.type === "function_result") && part.functionName.startsWith("omega_");
}

/**
 * Server history after an interrupted turn, keeping the Omega calls and results
 * this client already saw but the server did not persist. A save or test that
 * happened must stay visible, or the user would repeat it.
 */
export function mergeRecoveredHistory(current: ConversationMessage[], history: ConversationMessage[]): ConversationMessage[] {
  const recovered = history.map(message => {
    const local = current.find(item => item.id === message.id);
    const missing = local?.contents.filter(part => isOmegaToolPart(part) &&
      !message.contents.some(item => item.type === part.type && item.callId === part.callId)) ?? [];
    return missing.length ? {...message, contents: [...message.contents, ...missing]} : message;
  });
  const unpersisted = current.filter(message => !history.some(item => item.id === message.id) &&
    message.contents.some(part => part.type === "function_result" && isOmegaToolPart(part)));
  return [...recovered, ...unpersisted];
}

/** Drafts Wingman confirmed saving, in conversation order, keyed by the tool call that saved them. */
export function confirmedDraftSaves(messages: ConversationMessage[]): {callId: string; workflow: SavedOmegaWorkflow}[] {
  return messages.flatMap(message => message.contents.flatMap(part => {
    if (part.type !== "function_result" || part.functionName !== SAVE_DRAFT_TOOL) return [];
    const result = parseOmegaResult(part.result);
    if (!result?.ok || result.status >= 400) return [];
    const body = record.safeParse(result.body);
    const saved = body.success ? savedWorkflow.safeParse(body.data.data) : null;
    return saved?.success ? [{callId: part.callId, workflow: saved.data}] : [];
  }));
}

/**
 * Activity label for a refused Omega result. A workflow check is compiler
 * feedback before the status is considered, so an auth or server code on that
 * tool still names the graph problem.
 */
function failedOmegaActivityLabel(functionName: string, status: number): string {
  if (functionName === "omega_checkcustomerworkflow") return "compilerFeedback";
  if (status === 401) return "signInAgain";
  if (status === 403) return "noAccess";
  if (status >= 500) return "serviceUnavailable";
  return "requestFailed";
}

/**
 * Terminal trace events replace the run status. A start only fills a run that
 * is still unknown, so it cannot undo a failure or a completion already recorded.
 */
function statusAfterTrace(current: OmegaRunStatus, trace: {type: string; isSuccess?: boolean}): OmegaRunStatus {
  switch (trace.type) {
    case "workflow_failed":
    case "workflow_timed_out":
      return "failed";
    case "workflow_cancelled":
      return "cancelled";
    case "workflow_completed":
      return trace.isSuccess === false ? "failed" : "completed";
    case "workflow_started":
      return current === "unknown" ? "running" : current;
    default:
      return current;
  }
}

type OmegaPresentationState = {
  workflows: Map<string, SavedOmegaWorkflow>;
  latestSavedWorkflowId?: string;
  runs: Map<string, OmegaRun>;
  activities: OmegaActivity[];
  calls: Map<string, Record<string, unknown>>;
};

function callBody(state: OmegaPresentationState, callId: string) {
  const parsed = record.safeParse(state.calls.get(callId)?.body);
  return parsed.success ? parsed.data : undefined;
}

function collectSavedWorkflow(state: OmegaPresentationState, part: MessageContentFunctionResult, body: Record<string, unknown>) {
  const saved = savedWorkflow.safeParse(body.data);
  if (!saved.success) {
    state.activities.push({id: part.callId, label: "unreadableSave", error: true});
    return;
  }
  state.workflows.set(saved.data.id, saved.data);
  if (part.functionName === SAVE_DRAFT_TOOL) state.latestSavedWorkflowId = saved.data.id;
}

function collectStoppedSession(state: OmegaPresentationState, callId: string) {
  const id = callBody(state, callId)?.sessionId;
  if (typeof id !== "string") return;
  const previous = state.runs.get(id);
  state.runs.set(id, {id, workflowId: previous?.workflowId, status: "stopping", traces: previous?.traces ?? []});
}

function collectDebugSession(state: OmegaPresentationState, callId: string, body: Record<string, unknown>) {
  const input = callBody(state, callId);
  const id = body.sessionId ?? input?.sessionId;
  const parsedState = stateSchema.safeParse(body.state);
  if (typeof id !== "string" || !parsedState.success) {
    state.activities.push({id: callId, label: "unrecognizedRunState", error: true});
    return;
  }
  const previous = state.runs.get(id);
  const workflowId = previous?.workflowId ?? (typeof input?.workflowId === "string" ? input.workflowId : undefined);
  state.runs.set(id, {id, workflowId, status: parsedState.data.status, snapshot: body.state, traces: previous?.traces ?? []});
}

function collectRunTraces(state: OmegaPresentationState, callId: string, body: Record<string, unknown>) {
  const traces = z.array(traceSchema).safeParse(body.data);
  if (!traces.success) {
    state.activities.push({id: callId, label: "unreadableTraces", error: true});
    return;
  }
  for (const trace of [...traces.data].sort((a, b) => a.stepIndex - b.stepIndex)) {
    const run: OmegaRun = state.runs.get(trace.runId) ?? {id: trace.runId, workflowId: trace.workflowId, status: "unknown", traces: []};
    run.workflowId = trace.workflowId;
    run.traces = [...run.traces.filter(t => t.stepIndex !== trace.stepIndex), trace];
    run.status = statusAfterTrace(run.status, trace);
    state.runs.set(run.id, run);
  }
}

function collectResultBody(state: OmegaPresentationState, part: MessageContentFunctionResult, body: Record<string, unknown>) {
  switch (part.functionName) {
    case SAVE_DRAFT_TOOL:
    case "omega_fetchcustomerworkflow":
      collectSavedWorkflow(state, part, body);
      break;
    case "omega_checkcustomerworkflow":
      state.activities.push({id: part.callId, label: "compilerChecked", detail: body});
      break;
    case "omega_startdebugsession":
    case "omega_rundebugsession":
    case "omega_stepdebugsession":
      collectDebugSession(state, part.callId, body);
      break;
    case "omega_fetchworkflowrun":
    case "omega_fetchlastworkflowruns":
      collectRunTraces(state, part.callId, body);
      break;
  }
}

function collectOmegaResult(state: OmegaPresentationState, part: MessageContentFunctionResult) {
  const result = parseOmegaResult(part.result);
  if (!result) {
    const failure = z.object({error: z.string()}).safeParse(part.result);
    state.activities.push(failure.success
      ? {id: part.callId, label: "toolFailed", params: {error: failure.data.error}, error: true}
      : {id: part.callId, label: "unreadableResult", error: true});
    return;
  }
  if (!result.ok || result.status >= 400) {
    state.activities.push({
      id: part.callId,
      label: failedOmegaActivityLabel(part.functionName, result.status),
      params: {status: result.status},
      detail: result.body,
      error: true,
    });
    return;
  }
  if (part.functionName === "omega_stopdebugsession" && result.status === 204) {
    collectStoppedSession(state, part.callId);
    return;
  }
  const body = record.safeParse(result.body);
  if (!body.success) {
    state.activities.push({id: part.callId, label: "unrecognizedResponse", error: true});
    return;
  }
  collectResultBody(state, part, body.data);
}

export type OmegaPresentation = ReturnType<typeof collectOmegaPresentation>;

/** Pure projection of confirmed results; never executes or saves anything. */
export function collectOmegaPresentation(messages: ConversationMessage[]) {
  const state: OmegaPresentationState = {
    workflows: new Map(), runs: new Map(), activities: [], calls: new Map(),
  };
  const parts = messages.flatMap(message => message.contents);
  const completed = new Set(parts.filter(part => part.type === "function_result").map(part => part.callId));
  for (const part of parts) {
    if (!isOmegaToolPart(part) || !phases[part.functionName]) continue;
    if (part.type === "function_call") {
      state.calls.set(part.callId, part.parameters);
      if (!completed.has(part.callId)) state.activities.push({id: part.callId, label: phases[part.functionName], pending: true});
    } else {
      collectOmegaResult(state, part);
    }
  }
  // A later read of a reference workflow must not displace the one being built.
  return {workflows: [...state.workflows.values()], runs: [...state.runs.values()], activities: state.activities,
    defaultWorkflowId: state.latestSavedWorkflowId ?? [...state.workflows.keys()].at(-1)};
}
